import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button, Modal, Checkbox } from 'antd';
import { t } from '../../i18n/i18n';
import {
  acceptAllCookies,
  acceptSelectedCategories,
  CookieCategories,
  hasFullConsent,
  initializeConsentWithSync,
} from '../../services/cookieConsentService';
import './CookieConsentBanner.scss';

const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState({
    [CookieCategories.ANALYTICS]: true,
    [CookieCategories.MARKETING]: true,
    [CookieCategories.FUNCTIONAL]: true,
  });

  const { authenticated: isAuthenticated } = useSelector((state) => state.auth);

  useEffect(() => {
    // Initialize consent with sync when component mounts or auth changes
    const initConsent = async () => {
      try {
        const consent = await initializeConsentWithSync(isAuthenticated);

        // Ocultar banner si hay consentimiento (timestamp existe)
        // El banner se muestra solo si NO hay consentimiento previo
        if (consent && consent.timestamp && hasFullConsent()) {
          setShowBanner(false);
        } else {
          // No hay consentimiento previo, mostrar banner
          setShowBanner(true);
        }

        setIsInitialized(true);
      } catch (error) {
        console.error('Error initializing cookie consent:', error);
        // Show banner on error to be safe
        setShowBanner(true);
        setIsInitialized(true);
      }
    };

    initConsent();
  }, [isAuthenticated]);

  useEffect(() => {
    // Escuchar evento de actualización de consentimiento
    const handleConsentUpdate = () => {
      // Cuando se actualiza el consentimiento desde el backend, ocultar banner
      setShowBanner(false);
    };

    window.addEventListener('cookie-consent-updated', handleConsentUpdate);

    return () => {
      window.removeEventListener('cookie-consent-updated', handleConsentUpdate);
    };
  }, []);

  const handleAcceptAll = async () => {
    try {
      await acceptAllCookies();
      setShowBanner(false);
    } catch (error) {
      console.error('Error accepting cookies:', error);
      // Still hide banner even if backend sync fails
      setShowBanner(false);
    }
  };

  const handleCustomize = () => {
    setShowDetails(true);
  };

  const handleSavePreferences = async () => {
    try {
      await acceptSelectedCategories(selectedCategories);
      setShowDetails(false);
      setShowBanner(false);
    } catch (error) {
      console.error('Error saving preferences:', error);
      // Still hide banner even if backend sync fails
      setShowDetails(false);
      setShowBanner(false);
    }
  };

  const handleCategoryChange = (category, checked) => {
    setSelectedCategories({
      ...selectedCategories,
      [category]: checked,
    });
  };

  // Don't show banner until initialized
  if (!isInitialized || !showBanner) return null;

  return (
    <>
      <div className="cookie-consent-banner">
        <div className="cookie-consent-content">
          <div className="cookie-consent-text">
            <h3>{t('COOKIE_CONSENT_TITLE')}</h3>
            <p>{t('COOKIE_CONSENT_DESCRIPTION')}</p>
          </div>
          <div className="cookie-consent-actions">
            <Button onClick={handleCustomize} className="customize-button">
              {t('COOKIE_CONSENT_CUSTOMIZE')}
            </Button>
            <Button type="primary" onClick={handleAcceptAll} className="accept-button">
              {t('COOKIE_CONSENT_ACCEPT')}
            </Button>
          </div>
        </div>
      </div>

      <Modal
        title={t('COOKIE_CONSENT_PREFERENCES_TITLE')}
        open={showDetails}
        onCancel={() => setShowDetails(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowDetails(false)}>
            {t('COOKIE_CONSENT_CANCEL')}
          </Button>,
          <Button key="save" type="primary" onClick={handleSavePreferences}>
            {t('COOKIE_CONSENT_SAVE_PREFERENCES')}
          </Button>,
        ]}
        width={600}
      >
        <div className="cookie-categories">
          <div className="cookie-category">
            <Checkbox checked disabled>
              <strong>{t('COOKIE_CATEGORY_NECESSARY')}</strong>
            </Checkbox>
            <p className="category-description">{t('COOKIE_CATEGORY_NECESSARY_DESC')}</p>
          </div>

          <div className="cookie-category">
            <Checkbox
              checked={selectedCategories[CookieCategories.ANALYTICS]}
              onChange={(e) => handleCategoryChange(CookieCategories.ANALYTICS, e.target.checked)}
            >
              <strong>{t('COOKIE_CATEGORY_ANALYTICS')}</strong>
            </Checkbox>
            <p className="category-description">{t('COOKIE_CATEGORY_ANALYTICS_DESC')}</p>
          </div>

          <div className="cookie-category">
            <Checkbox
              checked={selectedCategories[CookieCategories.MARKETING]}
              onChange={(e) => handleCategoryChange(CookieCategories.MARKETING, e.target.checked)}
            >
              <strong>{t('COOKIE_CATEGORY_MARKETING')}</strong>
            </Checkbox>
            <p className="category-description">{t('COOKIE_CATEGORY_MARKETING_DESC')}</p>
          </div>

          <div className="cookie-category">
            <Checkbox
              checked={selectedCategories[CookieCategories.FUNCTIONAL]}
              onChange={(e) => handleCategoryChange(CookieCategories.FUNCTIONAL, e.target.checked)}
            >
              <strong>{t('COOKIE_CATEGORY_FUNCTIONAL')}</strong>
            </Checkbox>
            <p className="category-description">{t('COOKIE_CATEGORY_FUNCTIONAL_DESC')}</p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CookieConsentBanner;
